type ResetStateHandler = () => void | Promise<void>;
type SessionExpiredHandler = () => void;
type RedirectHandler = () => void;

let resetStateOnUnauthorized: ResetStateHandler | null = null;
let sessionExpiredOnUnauthorized: SessionExpiredHandler | null = null;
let redirectOnUnauthorized: RedirectHandler | null = null;
let unauthorizedHandlingStarted = false;

const NO_REDIRECT_AUTH_ENDPOINTS = [
  "/remit/auth/sendMobileOtp",
  "/remit/auth/sendEmailOtp",
  "/remit/auth/resendOtp",
  "/remit/auth/validateOtp",
  "/remit/auth/forgotPassword",
  "/remit/auth/signin",
  "/remit/auth/sign-in",
  "/remit/user/verifyQuote",
];

const isSignInEndpoint = (url?: string) => Boolean(url?.includes("/remit/auth/signin"));

export const shouldSkipUnauthorizedRedirect = (url?: string) =>
  NO_REDIRECT_AUTH_ENDPOINTS.some((endpoint) => url?.includes(endpoint));

const SESSION_ESTABLISHED_KEY = "appSessionEstablished";

export const markAppSessionEstablished = () => {
  if (typeof window !== "undefined") {
    sessionStorage.setItem(SESSION_ESTABLISHED_KEY, "true");
  }
};

export const clearAppSessionEstablished = () => {
  if (typeof window !== "undefined") {
    sessionStorage.removeItem(SESSION_ESTABLISHED_KEY);
  }
};

const isAppSessionEstablished = () =>
  typeof window !== "undefined" && sessionStorage.getItem(SESSION_ESTABLISHED_KEY) === "true";

export const resetUnauthorizedHandlingGuard = () => {
  unauthorizedHandlingStarted = false;
};

export const registerUnauthorizedHandlers = (handlers: {
  onResetState?: ResetStateHandler;
  onSessionExpired?: SessionExpiredHandler;
  onRedirect?: RedirectHandler;
}) => {
  resetStateOnUnauthorized = handlers.onResetState ?? null;
  sessionExpiredOnUnauthorized = handlers.onSessionExpired ?? null;
  redirectOnUnauthorized = handlers.onRedirect ?? null;
};

export const handleUnauthorized = async () => {
  if (typeof window === "undefined") return;
  if (unauthorizedHandlingStarted) return;

  // Only show session-expired UX after a successful login in this tab — not on bootstrap/401 checks.
  if (!isAppSessionEstablished()) {
    return;
  }

  unauthorizedHandlingStarted = true;

  if (sessionExpiredOnUnauthorized) {
    sessionExpiredOnUnauthorized();
    return;
  }

  await completeUnauthorizedLogout();
};

/** Clears client session and redirects — only after user confirms session-expired modal. */
export const completeUnauthorizedLogout = async () => {
  clearAppSessionEstablished();
  resetUnauthorizedHandlingGuard();
  await resetStateOnUnauthorized?.();

  if (redirectOnUnauthorized) {
    redirectOnUnauthorized();
    return;
  }

  window.location.href = "/auth/sign-in";
};

export const shouldHandleUnauthorizedForRequest = (url?: string, httpStatus?: number) => {
  if (httpStatus === 401 && !shouldSkipUnauthorizedRedirect(url) && !isSignInEndpoint(url)) {
    return true;
  }
  return false;
};

export const shouldHandleUnauthorizedErrorCode = (code?: number, url?: string) => {
  if (code === 9999 && !shouldSkipUnauthorizedRedirect(url) && !isSignInEndpoint(url)) {
    return true;
  }
  return false;
};
